import type {
  ImprovProfile,
  ImprovBrief,
  RhythmCatalog,
  RhythmCatalogEntry,
  ResolvedBrief,
  FieldPolicy,
  AudienceMode,
  ResolverPolicy,
  PerformerStatus,
} from '@stage/contracts';
import { newRequestId, parseError } from './client.js';

/**
 * "Create a Song" improv API client (M5, §20): improv-profile configuration (§20.10), rhythm
 * catalogs (§20.9), and performer review/editing of improv briefs (§20.13/§20.14). Shares the
 * {@link ApiError}/`ErrorEnvelope` contract and bearer-token pattern of {@link createAdminClient}.
 *
 * The client never stores tokens: it reads the current access token through the injected getter on
 * each request so silent refresh (§32.3) is transparent to callers.
 */

const BASE = '/v1';

/** Normalized resolved-field types (§20.12) — the `ResolvedBrief` sub-objects, non-optional. */
export type ResolvedTempo = NonNullable<ResolvedBrief['tempo']>;
export type ResolvedKey = NonNullable<ResolvedBrief['key']>;
export type ResolvedMeter = NonNullable<ResolvedBrief['meter']>;
export type ResolvedRhythm = NonNullable<ResolvedBrief['rhythm']>;

/**
 * Body for `POST /v1/artists/{artistId}/improv-profiles` and (partially) `PATCH
 * /v1/improv-profiles/{profileId}` (§20.10).
 */
export interface ProfileInput {
  name: string;
  audienceMode: AudienceMode;
  advancedDrilldown: boolean;
  fieldPolicies: FieldPolicy[];
  resolverPolicy: ResolverPolicy;
  summaryTemplate?: string;
  deviceProfileRef?: string | null;
}

/** Body for `POST /v1/artists/{artistId}/rhythm-catalogs` (§20.9). */
export interface CatalogInput {
  name: string;
  deviceProfileKey: string;
  entries?: RhythmCatalogEntry[];
}

/** Body for `PATCH /v1/rhythm-catalogs/{catalogId}` — send only what changed. */
export interface CatalogPatch {
  name?: string;
  status?: 'active' | 'archived';
}

/** Result of `POST /v1/rhythm-catalogs/{catalogId}/import`. */
export interface CatalogImportResult {
  imported: number;
  catalog: RhythmCatalog;
}

/**
 * Body for `PATCH /v1/gigs/{gigId}/improv-briefs/{briefId}` (§20.13). Every field optional — send
 * only what the performer changed. Editing never touches the immutable `rawChoices`.
 */
export interface BriefPatch {
  tempo?: Partial<ResolvedTempo>;
  key?: ResolvedKey;
  meter?: ResolvedMeter;
  rhythm?: Partial<ResolvedRhythm>;
  topic?: string | null;
  privatePerformerNote?: string | null;
  status?: PerformerStatus;
}

export interface ImprovClient {
  listProfiles(artistId: string): Promise<ImprovProfile[]>;
  createProfile(artistId: string, input: ProfileInput): Promise<ImprovProfile>;
  getProfile(profileId: string): Promise<ImprovProfile>;
  updateProfile(profileId: string, patch: Partial<ProfileInput>): Promise<ImprovProfile>;
  /** Publish a new version: bumps `profileVersion` and sets `status: 'active'` (§20.10). */
  publishProfileVersion(profileId: string): Promise<ImprovProfile>;
  /** Pin a published profile to a gig so the audience form uses it. */
  assignProfileToGig(gigId: string, profileId: string): Promise<ImprovProfile>;

  listCatalogs(artistId: string): Promise<RhythmCatalog[]>;
  createCatalog(artistId: string, input: CatalogInput): Promise<RhythmCatalog>;
  updateCatalog(catalogId: string, patch: CatalogPatch): Promise<RhythmCatalog>;
  importCatalogEntries(
    catalogId: string,
    entries: RhythmCatalogEntry[],
  ): Promise<CatalogImportResult>;

  listBriefs(gigId: string): Promise<ImprovBrief[]>;
  patchBrief(gigId: string, briefId: string, patch: BriefPatch): Promise<ImprovBrief>;
  /**
   * Derive a new brief: keep this brief's (path `briefId`) musical settings and take the topic from
   * `topicFromBriefId`. The response has `derivedFromBriefId` set (§20.13).
   */
  deriveBrief(gigId: string, briefId: string, topicFromBriefId: string): Promise<ImprovBrief>;
}

/**
 * Create an improv client bound to a live access-token getter, mirroring {@link createAdminClient}.
 * The getter is read per request so silent refresh is transparent (§32.3).
 */
export function createImprovClient(getAccessToken: () => string | null): ImprovClient {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = getAccessToken();
    const headers = new Headers(init?.headers);
    headers.set('accept', 'application/json');
    headers.set('x-request-id', newRequestId());
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init?.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    const res = await fetch(`${BASE}${path}`, { ...init, headers });
    if (!res.ok) throw await parseError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  const enc = encodeURIComponent;

  return {
    async listProfiles(artistId) {
      const body = await request<{ items: ImprovProfile[] }>(
        `/artists/${enc(artistId)}/improv-profiles`,
      );
      return body.items;
    },
    createProfile(artistId, input) {
      return request<ImprovProfile>(`/artists/${enc(artistId)}/improv-profiles`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    getProfile(profileId) {
      return request<ImprovProfile>(`/improv-profiles/${enc(profileId)}`);
    },
    updateProfile(profileId, patch) {
      return request<ImprovProfile>(`/improv-profiles/${enc(profileId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
    publishProfileVersion(profileId) {
      return request<ImprovProfile>(`/improv-profiles/${enc(profileId)}/versions`, {
        method: 'POST',
      });
    },
    assignProfileToGig(gigId, profileId) {
      return request<ImprovProfile>(`/gigs/${enc(gigId)}/improv-profile`, {
        method: 'PUT',
        body: JSON.stringify({ profileId }),
      });
    },

    async listCatalogs(artistId) {
      const body = await request<{ items: RhythmCatalog[] }>(
        `/artists/${enc(artistId)}/rhythm-catalogs`,
      );
      return body.items;
    },
    createCatalog(artistId, input) {
      return request<RhythmCatalog>(`/artists/${enc(artistId)}/rhythm-catalogs`, {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    updateCatalog(catalogId, patch) {
      return request<RhythmCatalog>(`/rhythm-catalogs/${enc(catalogId)}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
    importCatalogEntries(catalogId, entries) {
      return request<CatalogImportResult>(`/rhythm-catalogs/${enc(catalogId)}/import`, {
        method: 'POST',
        body: JSON.stringify({ entries }),
      });
    },

    async listBriefs(gigId) {
      const body = await request<{ items: ImprovBrief[] }>(`/gigs/${enc(gigId)}/improv-briefs`);
      return body.items;
    },
    patchBrief(gigId, briefId, patch) {
      return request<ImprovBrief>(
        `/gigs/${enc(gigId)}/improv-briefs/${enc(briefId)}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      );
    },
    deriveBrief(gigId, briefId, topicFromBriefId) {
      return request<ImprovBrief>(
        `/gigs/${enc(gigId)}/improv-briefs/${enc(briefId)}/derive`,
        { method: 'POST', body: JSON.stringify({ topicFromBriefId }) },
      );
    },
  };
}
